import {
  DASHBOARD_ARTIFACTS_SEGMENT,
  DASHBOARDS_CATALOG_BASE_PATH,
  getDashboardByPathname,
  getDashboardBySlug,
} from "./catalog";

export const PDF_EXPORT_CLASS = "pdf-exporting";
export const PDF_PAGE_STYLE_ID = "single-page-pdf-size";
export const PDF_HEIGHT_HEADROOM_PX = 64;
export const OPERATORS_PRODUCTION_ORIGIN = "https://operators.craniometrix-internal.com";

const DASHBOARD_CONTENT_SELECTOR = "[data-dashboard-content] > *";

export type PdfExportPageSize = {
  height: number;
  width: number;
};

type PdfExportMeasuredElement = {
  getBoundingClientRect?: () => { width: number };
  scrollWidth?: number;
};

type PdfExportDocument = {
  body?: { scrollHeight?: number; scrollWidth?: number } | null;
  createElement?: (tagName: string) => {
    id?: string;
    remove?: () => void;
    textContent?: string | null;
  };
  documentElement: {
    classList: {
      add: (className: string) => void;
      remove: (className: string) => void;
    };
    clientHeight: number;
    clientWidth: number;
    scrollHeight: number;
    scrollWidth: number;
  };
  fonts?: { ready?: Promise<unknown> };
  getElementById?: (id: string) => { remove?: () => void } | null;
  head?: { append: (node: unknown) => void };
  querySelector?: (selector: string) => PdfExportMeasuredElement | null;
  title: string;
};

type PdfExportWindow = {
  addEventListener: (type: string, listener: () => void) => void;
  clearTimeout: (id: number) => void;
  location?: { hash: string; pathname: string; search: string };
  print: () => void;
  removeEventListener: (type: string, listener: () => void) => void;
  requestAnimationFrame?: (callback: () => void) => number;
  setTimeout: (handler: () => void, timeout: number) => number;
};

function normalizePathname(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

/** Wait for two animation frames so export-only layout styles have settled. */
export function nextPaint(win: PdfExportWindow): Promise<void> {
  return new Promise((resolve) => {
    const frame =
      win.requestAnimationFrame ??
      ((callback) => {
        callback();
        return 0;
      });
    frame(() => {
      frame(() => {
        resolve();
      });
    });
  });
}

export function shouldShowPdfExport(pathname: string): boolean {
  const normalized = normalizePathname(pathname);
  if (normalized === DASHBOARDS_CATALOG_BASE_PATH) {
    return false;
  }
  const prefix = `${DASHBOARDS_CATALOG_BASE_PATH}/`;
  if (!normalized.startsWith(prefix)) {
    return false;
  }
  const slug = normalized.slice(prefix.length).split("/")[0] ?? "";
  // Artifact pages render their own control so the PDF carries the spec title.
  if (!slug || slug === DASHBOARD_ARTIFACTS_SEGMENT) {
    return false;
  }
  const entry = getDashboardBySlug(slug);
  if (entry) {
    return entry.pdfExport !== false;
  }
  return true;
}

export function getPdfExportTitle(pathname: string): string {
  const entry = getDashboardByPathname(pathname);
  if (entry) {
    return `${entry.label} - Dashboards`;
  }
  const normalized = normalizePathname(pathname);
  const prefix = `${DASHBOARDS_CATALOG_BASE_PATH}/`;
  if (normalized.startsWith(prefix)) {
    const slug = normalized.slice(prefix.length).split("/")[0] ?? "";
    if (slug) {
      return `${slug} - Dashboards`;
    }
  }
  return "Dashboards";
}

/** Measure the current dashboard content for a single-page PDF sheet. */
export function getDocumentPageSize(doc: PdfExportDocument): PdfExportPageSize {
  const root = doc.documentElement;
  const body = doc.body;
  const dashboard = doc.querySelector?.(DASHBOARD_CONTENT_SELECTOR);
  const dashboardRect = dashboard?.getBoundingClientRect?.();
  const documentWidth = Math.ceil(
    Math.max(root.clientWidth, root.scrollWidth, body?.scrollWidth || 0),
  );
  const contentWidth = Math.ceil(Math.max(dashboardRect?.width || 0, dashboard?.scrollWidth || 0));

  return {
    width: contentWidth || documentWidth,
    height:
      Math.ceil(Math.max(root.clientHeight, root.scrollHeight, body?.scrollHeight || 0)) +
      PDF_HEIGHT_HEADROOM_PX,
  };
}

/** Build the temporary CSS page rule used by the browser print dialog. */
export function singlePageRule({ width, height }: PdfExportPageSize): string {
  return `@page { size: ${width}px ${height}px; margin: 0; }`;
}

function readWindowLocation(): { hash: string; pathname: string; search: string } {
  const location = (
    globalThis as { location?: { hash?: string; pathname?: string; search?: string } }
  ).location;
  return {
    hash: location?.hash ?? "",
    pathname: location?.pathname ?? "",
    search: location?.search ?? "",
  };
}

/** Map the current local route to its production Operators dashboard URL. */
export function getProductionDashboardUrl(
  location: { hash: string; pathname: string; search: string } = readWindowLocation(),
): string {
  return `${OPERATORS_PRODUCTION_ORIGIN}${location.pathname}${location.search || ""}${location.hash || ""}`;
}

/** Build a lowercase, filename-style PDF title with the local calendar date. */
export function getPdfDocumentTitle(title: string, now = new Date()): string {
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  const slug = String(title)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  return `${slug}_${year}-${month}-${day}`;
}

function getBrowserDocument(): PdfExportDocument | undefined {
  if (typeof document === "undefined") {
    return undefined;
  }
  return document as unknown as PdfExportDocument;
}

function getBrowserWindow(): PdfExportWindow | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }
  return window as unknown as PdfExportWindow;
}

export async function exportDashboardPdf(input: {
  doc?: PdfExportDocument;
  onSettled?: () => void;
  title: string;
  win?: PdfExportWindow;
}): Promise<void> {
  const doc = input.doc ?? getBrowserDocument();
  const win = input.win ?? getBrowserWindow();
  if (!doc || !win) {
    input.onSettled?.();
    return;
  }
  doc.documentElement.classList.add(PDF_EXPORT_CLASS);

  let fallbackTimeoutId: number | undefined;
  let cleaned = false;
  const previousDocumentTitle = doc.title;

  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    if (fallbackTimeoutId !== undefined) {
      win.clearTimeout(fallbackTimeoutId);
    }
    win.removeEventListener("afterprint", cleanup);
    doc.documentElement.classList.remove(PDF_EXPORT_CLASS);
    doc.getElementById?.(PDF_PAGE_STYLE_ID)?.remove?.();
    doc.title = previousDocumentTitle;
    input.onSettled?.();
  };

  try {
    await doc.fonts?.ready;
    await nextPaint(win);

    const pageStyle = doc.createElement?.("style") ?? { id: "", textContent: "" };
    pageStyle.id = PDF_PAGE_STYLE_ID;
    pageStyle.textContent = singlePageRule(getDocumentPageSize(doc));
    doc.head?.append(pageStyle);

    doc.title = getPdfDocumentTitle(input.title);
    win.addEventListener("afterprint", cleanup);
    fallbackTimeoutId = win.setTimeout(cleanup, 1_000);
    win.print();
  } catch (error) {
    cleanup();
    console.error("PDF export failed:", error);
  }
}
